import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  AlertCircle,
  ChevronLeft,
  Clock,
  Pencil,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react'
import type { AppDispatch, RootState } from '@/redux'
import { getDashboard } from '@/services/dashboardsService'
import { loadChatSession } from '@/services/DashboardChatService'
import { loadPanels } from '@/services/DashboardPanelsService'
import { loadDashboardData, handleSyncComplete } from '@/services/DashboardSyncService'
import { chatSessionActions } from '@/redux/slice/chatSessionSlice'
import { dashboardsActions } from '@/redux/slice/dashboardsSlice'
import { dashboardPanelsActions } from '@/redux/slice/dashboardPanelsSlice'
import { dashboardSyncActions, selectSyncError, selectIsSyncing as selectLegacySyncIsSyncing, selectPanelDataError } from '@/redux/slice/dashboardSyncSlice'
import {
  dashboardCreationActions,
  selectHarvestContentState,
  selectHarvestFailure,
  selectCurrentFlowState,
} from '@/redux/slice/dashboardCreationSlice'
import {
  dashboardViewActions,
  selectFailedDomains,
  selectRateLimitedUntil,
  selectRateLimitVendor,
  selectAuthExpiredEnvelope,
  selectLastSyncAt,
  selectEditingPanelId,
  selectChatMode,
  selectDomainsFailed,
  selectShouldShowSyncErrorBanner,
} from '@/redux/slice/dashboardViewSlice'
import { DashboardSocketService } from '@/services/DashboardSocketService'
import { syncDashboardThunk, resetDashboardThunk, loadDashboardDataV1Thunk, retryDomainThunk, _resetPanelDataV1LoadSerial } from '@/services/DashboardViewService'
import { startHarvestThunk } from '@/services/HarvestService'
import { bootstrapEditSessionThunk, sendEditChatMessageThunk } from '@/services/dashboardEditChatService'
import {
  dashboardEditChatActions,
  selectEditSessionDashboardId,
  selectEditSessionId,
  selectEditFlowState,
  selectEditFlowContext,
} from '@/redux/slice/dashboardEditChatSlice'
import { DashboardsAPI } from '@/API/Dashboards'
import { Loading } from '@/components/custom/Loading/Loading'
import { ChatOverlay } from './components/ChatPanel/ChatOverlay'
import { AskTcsAiToggle } from './components/AskTcsAiToggle'
import { PanelCanvasV1 } from './components/PanelCanvas/PanelCanvasV1'
import { BuildingOverlay } from './components/BuildingOverlay/BuildingOverlay'
import { AuthExpiredBanner } from './components/AuthExpiredBanner/AuthExpiredBanner'
import { RateLimitBanner } from './components/RateLimitBanner'
import { LastSyncChip } from './components/LastSyncChip'
import { DomainErrorBannerStack } from './components/PanelCanvas/DomainErrorBanner'
import { SyncErrorBanner } from './components/SyncErrorBanner/SyncErrorBanner'
import { HarvestModal } from './components/harvest/HarvestModal'
import { CreateOrUpdateDashboardModal } from './components/CreateOrUpdateDashboardModal'
import { ConfirmationModal } from '@/components/custom/AlertDialogButton/ConfirmationModal'
import { ActionMenu } from '@/components/custom/Table/TableActionMenu'
import { Button } from '@/components/ui/button'
import { ButtonVariant } from '@/constants/button/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { selectResolveDomainDisplayName } from '@/redux/slice/dashboardDomainRegistrySlice'
import type {
  CreationStatus,
  InsightSocketPayload,
  SyncCreationDataReadyPayload,
  SyncDashboardCompletePayload,
} from '@/types/Dashboards.types'
import type { OutOfScopePrompt } from '@/types/DashboardV1.types'
import { cn } from '@/lib/utils'

/** localStorage key for AI chat panel open/collapsed state in viewing mode. */
const CHAT_OPEN_KEY = 'tcs_dashboard_chat_open'

/**
 * DashboardCanvas — the main dashboard viewing page (/dashboards/:id).
 *
 * Layout (per brand guide §36 — Dashboard Chat Surface v2 Overlay Pattern):
 *   - Canvas always occupies full width (PanelCanvasV1, no resize when chat opens).
 *   - AI chat is a position:fixed slide-over overlay (ChatOverlay, 460px / 380px).
 *   - "Ask TCS AI" toggle button in top-bar right (AskTcsAiToggle).
 *   - ⋮ menu: Reset + Delete ONLY (no Dashboard settings per Change D).
 *   - Sync button (outline variant) in top-bar right.
 *   - PanelCanvasV1 shows empty state when panels is [] (legacy era mid-creation dashboards).
 *
 * Creation flow lives at /dashboards/new → DashboardCreationShell (Slice 1.4, PR #322).
 * This route is always viewing-only — creation-mode branching was removed (bugbash 2026-04-27).
 *
 * Does NOT use .protectedPageLayout — uses the full-width canvas layout exception per brand guide.
 */
export const DashboardCanvas = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const { loading } = useSelector((state: RootState) => state.app)
  const { selectedDashboard, isLoading: dashboardLoading } = useSelector(
    (state: RootState) => state.dashboards
  )
  const { creationStatus, messages } = useSelector((state: RootState) => state.chatSession)
  // flowState lives in dashboardCreationSlice (dispatched by loadChatSession from
  // session.flowState on cold load). It is the reliable "creation is done" signal —
  // creationStatus only reaches 'complete' via the live confirmPanels path, not on
  // cold-load-after-creation. See Bug 18 fix comment on the bootstrap useEffect below.
  const flowState = useSelector(selectCurrentFlowState)
  // Memoized boolean: true when at least one panel_proposals message is present.
  // Used as an effect dep instead of the full messages array — setSession always
  // produces a new array reference (immer replaces the array), so putting messages
  // in effect deps would re-run the recovery-polling effect on every loadChatSession
  // response, restarting the interval even when nothing actionable changed.
  const hasProposals = useMemo(
    () => messages.some(m => m.type === 'panel_proposals'),
    [messages]
  )
  const { isBuilding } = useSelector((state: RootState) => state.dashboardPanels)
  const { panels } = useSelector((state: RootState) => state.dashboardPanels)
  // Registry-aware display-name resolver. Replaces the deleted
  // resolveDomainDisplayName helper from src/constants/domains.ts.
  const resolveDomainDisplayName = useSelector(selectResolveDomainDisplayName)
  // Memoized to prevent a new string reference on every render when panels haven't
  // changed. Without useMemo, panels.map().join() runs every render and, while the
  // resulting string is value-equal, any future refactor that widens the selector or
  // changes how panels is read could silently introduce an Effect 3 infinite loop.
  const panelIdsKey = useMemo(() => panels.map(p => p.id).join(','), [panels])
  const syncError = useSelector(selectSyncError)
  const isSyncing = useSelector(selectLegacySyncIsSyncing)
  const panelDataError = useSelector(selectPanelDataError)
  const failedDomains = useSelector(selectFailedDomains)
  const rateLimitedUntil = useSelector(selectRateLimitedUntil)
  const rateLimitVendor = useSelector(selectRateLimitVendor)
  const authExpiredEnvelope = useSelector(selectAuthExpiredEnvelope)
  const lastSyncAt = useSelector(selectLastSyncAt)
  // Slice 3.2 — credential-aware availability sync-error banner.
  // `domainsFailed` is populated by the V1 `sync:dashboard:complete` socket
  // handler when the BE reports domains whose backing credential probed as
  // `unavailable` (those domains were SKIPPED by the sync pipeline per the
  // §0.3 panel preservation invariant). `showSyncErrorBanner` is true when
  // domainsFailed is non-empty AND the user hasn't dismissed for this cycle.
  const domainsFailed = useSelector(selectDomainsFailed)
  const showSyncErrorBanner = useSelector(selectShouldShowSyncErrorBanner)
  const harvestFailure = useSelector(selectHarvestFailure)
  const editingPanelId = useSelector(selectEditingPanelId)
  const chatMode = useSelector(selectChatMode)

  // Edit session selectors — used to decide whether to bootstrap on overlay open
  const editSessionDashboardId = useSelector(selectEditSessionDashboardId)
  const editSessionId = useSelector(selectEditSessionId)
  // Slice 3.2: edit-mode sync socket handlers need current FSM state + context
  const editFlowState = useSelector(selectEditFlowState)
  const editFlowContext = useSelector(selectEditFlowContext)

  // PR 6: Human-readable stale date for the "Data as of [date]" note on panels.
  // Formatted as "Apr 22, 2026". Undefined when no sync time is recorded —
  // PanelRenderer guards on showStaleNote && staleAsOf so the note is suppressed.
  const staleAsOf = lastSyncAt
    ? new Date(lastSyncAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : undefined

  // v3: AI chat panel open state — persisted to localStorage
  // Default: open (true). User can collapse and the preference is remembered.
  const [isViewingChatOpen, setIsViewingChatOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(CHAT_OPEN_KEY)
      return stored === null ? true : stored === 'true'
    } catch {
      return true
    }
  })

  // v3: delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // Reset confirmation dialog — local state; no Redux needed (ephemeral UI toggle)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [isDeletingDashboard, setIsDeletingDashboard] = useState(false)

  // Slice E: Harvest modal open state — used for in-flow reconnect when AUTH_EXPIRED.
  const [reconnectModalOpen, setReconnectModalOpen] = useState(false)
  // Slice E: Whether the user has dismissed the AuthExpiredBanner.
  // Reset whenever dashboardError transitions to/from AUTH_EXPIRED so the banner
  // reappears on the next occurrence.
  const [authBannerDismissed, setAuthBannerDismissed] = useState(false)
  // Multi-banner stacking rule (CEO directive 2026-05-05, brand guide §34.3):
  // authExpiredEnvelope is now its own independent Redux slot — not derived from
  // a single discriminated dashboardError field. This means RateLimitBanner and
  // AuthExpiredBanner can be active simultaneously without one hiding the other.
  const authExpiredActive = authExpiredEnvelope != null

  // PR 6: domainIds currently being retried (disables per-banner Retry button)
  const [retryingDomainIds, setRetryingDomainIds] = useState<Set<string>>(new Set())

  // Local state: whether the user has dismissed the sync error banner
  const [bannerDismissed, setBannerDismissed] = useState(false)
  // Local state: whether the user has dismissed the panel data error banner
  const [panelErrorDismissed, setPanelErrorDismissed] = useState(false)
  const [isRenameDashboardModalOpen, setIsRenameDashboardModalOpen] = useState(false)

  // Persist AI chat open state to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_OPEN_KEY, String(isViewingChatOpen))
    } catch {
      // localStorage unavailable — ignore
    }
  }, [isViewingChatOpen])

  // Reset panel error dismissed state when panelDataError changes
  useEffect(() => {
    setPanelErrorDismissed(false)
  }, [panelDataError])

  // Slice E: Reset auth-expired banner dismissed state when the AUTH_EXPIRED error
  // clears (so the banner re-appears on the next occurrence).
  useEffect(() => {
    if (!authExpiredActive) {
      setAuthBannerDismissed(false)
    }
  }, [authExpiredActive])

  // Slice C: countdown state for rate-limited sync button.
  // Re-computes every second while rateLimitedUntil is active so the button
  // label ("Retry in Ns") stays current without a Redux round-trip per tick.
  const [rateLimitSecondsLeft, setRateLimitSecondsLeft] = useState(() =>
    rateLimitedUntil != null
      ? Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000))
      : 0
  )
  useEffect(() => {
    if (rateLimitedUntil == null) {
      setRateLimitSecondsLeft(0)
      return
    }
    const tick = () => {
      setRateLimitSecondsLeft(Math.max(0, Math.ceil((rateLimitedUntil - Date.now()) / 1000)))
    }
    tick() // sync immediately on mount / rateLimitedUntil change
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [rateLimitedUntil])

  // v2 overlay: inert management is now owned by ChatOverlay — removed from Canvas.

  // On route change, restore chat open state from localStorage (default: open)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CHAT_OPEN_KEY)
      setIsViewingChatOpen(stored === null ? true : stored === 'true')
    } catch {
      setIsViewingChatOpen(true)
    }
  }, [id])

  // Track 2: Auto-open chat panel when user enters panel-edit mode (mockup 21).
  // If the chat is collapsed and the user clicks "Edit with AI" on a panel,
  // the chat panel slides open to show the PanelContextBanner + edit greeting.
  useEffect(() => {
    if (chatMode === 'panel' && editingPanelId !== null) {
      setIsViewingChatOpen(true)
    }
  }, [chatMode, editingPanelId])

  // v1 panel data hydration: load panel display data after initial mount and after
  // each sync completion (tracked by markDashboardComplete → isSyncing: false).
  const { isSyncing: v1IsSyncing } = useSelector((state: RootState) => state.dashboardView)

  // Combined sync flag: gate banner visibility and dismiss-reset on either sync source
  const anySyncActive = isSyncing || v1IsSyncing

  // Reset banner dismissed state when any sync starts (legacy or v1)
  useEffect(() => {
    if (anySyncActive) {
      setBannerDismissed(false)
    }
  }, [anySyncActive])
  useEffect(() => {
    // Guard: wait until the selected dashboard entity in Redux matches the route id.
    // Without this guard, an in-flight getDashboard(B) call leaves selectedDashboard
    // momentarily null/stale (pointing at A), and this effect fires immediately on
    // `id` change — dispatching loadDashboardDataV1Thunk for the wrong dashboard or
    // before Redux state is coherent for the new dashboard. Matches the guard pattern
    // used by Effect 2 and Effect 3 below.
    // Fix: bugbash 2026-06-01 — symptoms 2 + 3.
    //
    // Note on layered defense: loadDashboardDataV1Thunk has its own in-thunk stale-id
    // guard (lines 252-253) that discards the response if the user navigated away
    // post-request. This effect-level guard is complementary — it prevents the network
    // request from being dispatched at all (saves a round-trip and closes the brief
    // window where A's request is in-flight while B is rendering).
    if (!token || !id || selectedDashboard?.id !== id) return
    // Load panel data on initial mount (when data may already exist in cache)
    // and when a sync completes (v1IsSyncing flips false → true → false).
    if (!v1IsSyncing) {
      void dispatch(loadDashboardDataV1Thunk(token, id))
    }
  }, [v1IsSyncing, token, id, selectedDashboard?.id, dispatch])

  // Polling fallback: when in fetching_data state, poll the session every 5 seconds.
  // The socket event (sync:creation:data_ready) can be missed if the connection drops
  // during a long fetch (e.g. 3+ minutes for large CampMinder datasets). 5-second
  // polling ensures the UI recovers quickly even when the socket event was lost.
  useEffect(() => {
    if (creationStatus !== 'fetching_data' || !token || !id) {
      return
    }
    const interval = setInterval(() => {
      dispatch(loadChatSession(token, id))
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [creationStatus, token, id, dispatch])

  // Recovery polling: when status is awaiting_confirmation but no proposals are loaded yet,
  // poll every 3 seconds until proposals appear. This handles the case where the socket
  // data_ready event fired and setCreationStatus('awaiting_confirmation') was dispatched,
  // but the subsequent loadChatSession call failed (expired token, network blip) leaving
  // the UI with the correct status but empty panel proposals.
  //
  // Uses the memoized hasProposals boolean (not the raw messages array) as the dep.
  // setSession always produces a new messages array reference even when content is
  // unchanged — putting messages directly in deps would restart the interval on every
  // loadChatSession response, collapsing the 3 s poll into an unconditional tight loop.
  useEffect(() => {
    if (creationStatus !== 'awaiting_confirmation' || hasProposals || !token || !id) return
    const interval = setInterval(() => {
      dispatch(loadChatSession(token, id))
    }, 3000)
    return () => clearInterval(interval)
  }, [creationStatus, hasProposals, token, id, dispatch])

  // Effect 1: on dashboard-id transition, clear stale session/panels state.
  // Always fetch the dashboard entity. Reset stale state ONLY when actually
  // navigating between two distinct dashboards — not on initial mount.
  //
  // BUG-02b + BUG-03a (bugbash 2026-05-01): the previous version of this
  // effect dispatched reset() unconditionally on every mount. When
  // DashboardCreationShell completed a build and navigated to
  // /dashboards/:id, syncDashboardThunk had ALREADY dispatched
  // setSyncing(true) — but reset() ran a tick later and clobbered it back
  // to initialState. Two downstream symptoms:
  //   (a) DomainProgressBanner never rendered (gated on isSyncing).
  //   (b) The sync:dashboard:complete handler eventually ran
  //       markDashboardComplete (sets isSyncing=false), but isSyncing was
  //       already false, so the false→true→false transition never
  //       happened — the v1IsSyncing-deps effect that re-fetches panel
  //       data on sync completion never re-fired. User had to hard-refresh
  //       the browser to see post-sync data.
  // The prevIdRef gate skips reset on first mount and on no-op
  // re-renders, so in-flight sync state set up by build → auto-sync is
  // preserved through the navigation.
  // v2 overlay: ref for the "Ask TCS AI" toggle button.
  // Focus returns here when the overlay closes (brand guide §36 Focus management).
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  const prevDashboardIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!token || !id) return
    if (prevDashboardIdRef.current && prevDashboardIdRef.current !== id) {
      dispatch(chatSessionActions.clearSession())
      dispatch(dashboardPanelsActions.clearPanels())
      dispatch(dashboardViewActions.reset())
      // Clear edit session on navigation (AC #6 — session persists across
      // open/close within the same dashboard, but clears on navigation away).
      dispatch(dashboardEditChatActions.clearEditSession())
      // Reset the module-level load serial so any in-flight loadDashboardDataV1Thunk
      // for the previous dashboard is invalidated immediately. Without this, a slow
      // response from dashboard A can win the serial race against dashboard B's
      // response and commit A's panelDataMap/domainStatus into B's view.
      // Fix: bugbash 2026-06-01 — symptoms 2 + 3.
      _resetPanelDataV1LoadSerial()
    }
    prevDashboardIdRef.current = id
    dispatch(getDashboard(token, id))
  }, [token, id, dispatch])

  // Effect 2: load chat + panels only after the dashboard entity for the current route is confirmed in state
  useEffect(() => {
    if (!token || !id || selectedDashboard?.id !== id) return
    dispatch(loadChatSession(token, id))
    dispatch(loadPanels(token, id))
  }, [token, id, selectedDashboard?.id, dispatch])

  // Effect 3: load panel data when dashboard is confirmed and panels exist
  useEffect(() => {
    if (!token || !id || selectedDashboard?.id !== id || panelIdsKey === '') return
    dispatch(loadDashboardData(token, id))
  }, [token, id, selectedDashboard?.id, panelIdsKey, dispatch])

  // Connect Socket.IO; register event handlers; disconnect on unmount
  useEffect(() => {
    if (!token || !id) return

    DashboardSocketService.connect(id, token)
    // Wire v1 domain-level events (sync:domain:*, sync:dashboard:complete) to Redux
    DashboardSocketService.subscribeV1Events(dispatch)

    const handleDataReady = (data: unknown) => {
      // Backend sends creationStatus on data_ready (e.g. awaiting_confirmation after
      // fetch, or awaiting_sources when no rows) so the spinner clears without waiting
      // for loadChatSession. It may also send dashboardName when an AI title was saved.
      const payload = data as SyncCreationDataReadyPayload
      if (payload?.creationStatus) {
        dispatch(chatSessionActions.setCreationStatus(payload.creationStatus as CreationStatus))
      }
      if (
        typeof payload?.dashboardName === 'string' &&
        payload.dashboardName.trim() !== '' &&
        id
      ) {
        dispatch(
          dashboardsActions.updateDashboardName({
            id,
            name: payload.dashboardName.trim(),
          })
        )
      }
      if (token && id) {
        dispatch(loadChatSession(token, id))
      }
    }

    const handleCreationComplete = () => {
      if (token && id) {
        dispatch(loadPanels(token, id))
      }
    }

    const handleCreationError = (data: unknown) => {
      const errorData = data as { error?: string }
      dispatch(chatSessionActions.setCreationStatus('awaiting_sources'))
      dispatch(
        chatSessionActions.setError(
          errorData?.error ?? 'An error occurred while fetching your data.'
        )
      )
      // Reload messages so the error chat bubble appended by the backend is visible
      if (token && id) {
        dispatch(loadChatSession(token, id))
      }
    }

    DashboardSocketService.on('sync:creation:data_ready', handleDataReady)
    DashboardSocketService.on('sync:creation:complete', handleCreationComplete)
    DashboardSocketService.on('sync:creation:error', handleCreationError)

    return () => {
      DashboardSocketService.off('sync:creation:data_ready', handleDataReady)
      DashboardSocketService.off('sync:creation:complete', handleCreationComplete)
      DashboardSocketService.off('sync:creation:error', handleCreationError)
      DashboardSocketService.unsubscribeV1Events()
      DashboardSocketService.disconnect()
    }
  }, [token, id, dispatch])

  // Slice 3 + 4: Socket.IO sync event handlers
  useEffect(() => {
    if (!token || !id || selectedDashboard?.id !== id) return

    const handleSyncDashboardComplete = (data: unknown) => {
      const payload = data as SyncDashboardCompletePayload
      dispatch(handleSyncComplete(token, id, payload))
    }

    // Note (Slice F bugbash 2026-05-05): the legacy `sync:source:complete`
    // listener was removed here. The BE now emits dashboard-era event names
    // (`sync:domain:progress` / `:complete` / `:error`) which are handled by
    // `DashboardSocketService.subscribeV1Events()` and dispatch
    // `markDomainComplete` / `setDomainError` to drive the LastSyncChip.

    const handleInsightsPanel = (data: unknown) => {
      if (
        !data ||
        typeof data !== 'object' ||
        typeof (data as InsightSocketPayload).dashboardId !== 'string' ||
        typeof (data as InsightSocketPayload).panelId !== 'string' ||
        typeof (data as InsightSocketPayload).insight !== 'string'
      ) {
        return
      }
      const payload = data as InsightSocketPayload
      if (payload.dashboardId !== id) return
      dispatch(dashboardPanelsActions.setPanelInsight({
        panelId: payload.panelId,
        insight: payload.insight,
      }))
    }

    DashboardSocketService.on('sync:dashboard:complete', handleSyncDashboardComplete)
    DashboardSocketService.on('sync:insights:panel', handleInsightsPanel)

    return () => {
      DashboardSocketService.off('sync:dashboard:complete', handleSyncDashboardComplete)
      DashboardSocketService.off('sync:insights:panel', handleInsightsPanel)
      dispatch(dashboardSyncActions.clearSync())
    }
  }, [selectedDashboard?.id, id, token, dispatch])

  // Edit-mode sync socket handlers (Slice 3.2 §7.3)
  // Mirrors the BE FSM update (edit:syncing → edit:awaiting_panels) to the FE
  // slice when sync completes or errors during an active filter-values edit.
  // The BE's onSyncComplete already updates the persisted session; this handler
  // makes the in-memory slice reflect that immediately so the chat input unlocks.
  //
  // NOTE: Adding a second listener for sync:dashboard:complete does NOT remove
  // or interfere with handleSyncDashboardComplete above — DashboardSocketService
  // supports multiple listeners per event. The two listeners are additive:
  // one updates dashboardViewSlice (canvas panels), the other updates
  // dashboardEditChatSlice (FSM state + synthetic chat message).
  useEffect(() => {
    if (!token || !id) return

    const handleEditSyncComplete = (data: unknown) => {
      if (!data || typeof data !== 'object' || !('dashboardId' in data)) return
      const payload = data as { dashboardId: string }
      if (
        payload.dashboardId !== id ||
        payload.dashboardId !== editSessionDashboardId
      ) return
      if (editFlowState?.mode !== 'edit' || editFlowState.state !== 'syncing') return
      /* istanbul ignore next — flowState and flowContext are always set together by setSession/hydrateEditSession/updateSessionFromMessageResponse; a state where flowState is edit:syncing but flowContext is null cannot be produced by the Redux slice. Guard retained as structural defense-in-depth. */
      if (!editFlowContext) return

      dispatch(
        dashboardEditChatActions.updateSessionFromMessageResponse({
          flowState: { mode: 'edit', state: 'awaiting_panels' },
          flowContext: { ...editFlowContext, state: 'awaiting_panels' },
        })
      )
      dispatch(
        dashboardEditChatActions.appendEditMessage({
          role: 'assistant',
          type: 'text',
          content: 'Sync complete — your dashboard is up to date.',
          timestamp: new Date().toISOString(),
        })
      )
    }

    const handleEditSyncError = (data: unknown) => {
      if (!data || typeof data !== 'object' || !('dashboardId' in data)) return
      const payload = data as { dashboardId: string }
      if (
        payload.dashboardId !== id ||
        payload.dashboardId !== editSessionDashboardId
      ) return
      if (editFlowState?.mode !== 'edit' || editFlowState.state !== 'syncing') return

      dispatch(
        dashboardEditChatActions.appendEditMessage({
          role: 'assistant',
          type: 'text',
          content: 'Sync failed — your filter change could not be applied. Try again, or change a different filter.',
          timestamp: new Date().toISOString(),
        })
      )
    }

    DashboardSocketService.on('sync:dashboard:complete', handleEditSyncComplete)
    DashboardSocketService.on('sync:dashboard:error', handleEditSyncError)

    return () => {
      DashboardSocketService.off('sync:dashboard:complete', handleEditSyncComplete)
      DashboardSocketService.off('sync:dashboard:error', handleEditSyncError)
    }
  }, [token, id, editFlowState, editFlowContext, editSessionDashboardId, dispatch])

  // BUG-02b + BUG-03a (bugbash 2026-05-01, second iteration): the previous
  // version of this file had a separate "clean up Redux state on unmount"
  // useEffect at this position whose cleanup dispatched reset() unconditionally.
  // React 18 Strict Mode (dev) runs every effect's cleanup once after the
  // initial mount as a stress-test. That meant: Canvas mounts → setSyncing(true)
  // is in Redux from buildDashboardThunk's auto-fired sync → Strict Mode invokes
  // this cleanup → reset() runs → isSyncing=false → banner never renders, sync:
  // dashboard:complete cannot fire the false→true→false transition that would
  // re-fetch panel data. The fix in Effect 1 (prevDashboardIdRef gate) was
  // necessary but not sufficient because this cleanup also clobbered state.
  //
  // Cleanup that destroys Redux state isn't Strict Mode-safe by design — React
  // 18 explicitly stress-tests cleanups and components must tolerate the double
  // invocation. The cross-dashboard state-clearing concern is already handled
  // by Effect 1's prevIdRef logic. Leaving stale state across a leave-route /
  // come-back cycle is harmless (the next dashboard mount cleans, the slice
  // reset on logout cleans the rest). So we remove the unmount cleanup
  // entirely rather than re-introduce a Strict-Mode-fragile gate.

  // PR 6: Reconnect CTA — dispatches startHarvestThunk and opens the HarvestModal.
  // appCode is always 'campminder' for v1 (only CampMinder is supported).
  // We await the thunk before opening the modal so that if startup fails
  // (harvestFailure set), we never open an indefinite-loader modal.
  const handleReconnect = useCallback(async () => {
    if (!token) return
    // Slice F bugbash 2026-05-06: do NOT clear the AUTH_EXPIRED slot when the
    // user clicks Reconnect. The previous behaviour ("clear immediately because
    // the credential issue is being actively addressed") caused a regression:
    // if the user closed the harvest modal mid-flow without completing, the
    // banner would be gone but the credential would still be invalid — and the
    // legacy compat banner would leak through with stale copy. The slot now
    // clears ONLY on a successful sync against the new credential
    // (DashboardSyncService.handleSyncComplete dispatches clearAuthExpired
    // when a vendor's domains all sync successfully). If the modal is closed
    // mid-flow, AuthExpiredBanner stays visible with the Reconnect CTA still
    // available to try again.
    dispatch(dashboardCreationActions.clearHarvest())
    await (dispatch as (action: unknown) => Promise<void>)(
      startHarvestThunk(token, 'campminder')
    )
    setReconnectModalOpen(true)
  }, [token, dispatch])

  // Slice F bugbash 2026-05-06: when harvest completes successfully, auto-fire
  // a dashboard sync against the new credential. The sync's per-vendor success
  // path (DashboardSyncService.handleSyncComplete) clears the AUTH_EXPIRED slot
  // when all vendor domains sync, dismissing the AuthExpiredBanner naturally.
  // If the new credential is also invalid (rare), the dispatcher re-classifies
  // and the banner re-renders with the fresh envelope. If harvest creates a
  // new credential UUID different from the panel's stored credentialId,
  // auto-association is a follow-on (the next sync would still fail until
  // panels are re-pointed at the new credential — tracked separately).
  const harvestContentState = useSelector(selectHarvestContentState)
  const prevHarvestContentStateRef = useRef(harvestContentState)
  useEffect(() => {
    const prev = prevHarvestContentStateRef.current
    prevHarvestContentStateRef.current = harvestContentState
    if (
      prev !== 'success' &&
      harvestContentState === 'success' &&
      token &&
      id
    ) {
      // Close the modal first so the user sees the auto-sync progress on
      // the canvas, not behind the "success" modal screen.
      setReconnectModalOpen(false)
      void dispatch(syncDashboardThunk(token, id))
    }
  }, [harvestContentState, token, id, dispatch])

  // Close the reconnect modal whenever harvestFailure becomes non-null while it is open.
  // Mirrors CredentialPickerHarvestBlock's pattern — covers both post-thunk and
  // mid-flow socket failure cases.
  useEffect(() => {
    if (harvestFailure && reconnectModalOpen) {
      setReconnectModalOpen(false)
    }
  }, [harvestFailure, reconnectModalOpen])

  // PR 6: Per-domain retry — disables the Retry button while in-flight.
  // Socket.IO drives the success/error state after the API call returns.
  const handleRetryDomain = useCallback(
    (domainId: string) => {
      if (!token || !id) return
      setRetryingDomainIds(prev => new Set([...prev, domainId]))
      void dispatch(retryDomainThunk(token, id, domainId))
        .finally(() => {
          setRetryingDomainIds(prev => {
            const next = new Set(prev)
            next.delete(domainId)
            return next
          })
        })
        // Error state is driven by Redux (Socket.IO domain:error event) — suppress the
        // promise rejection so it does not surface as an unhandled rejection.
        .catch(() => {})
    },
    [token, id, dispatch]
  )

  /**
   * Panel-level retry handler (YoY Slice 2.3 — MULTI_FETCH_PARTIAL_FAILURE).
   *
   * Fires a full dashboard re-sync when the user clicks "Retry" on a
   * PartialFailureCallout. This is the simplest correct behavior — it re-runs
   * all domain fetches, which will re-attempt the failed fan-out branch.
   *
   * Uses `syncDashboardThunk` (not the legacy `triggerSync`) so the chip-honesty
   * optimistic `startDomainFetch` loop fires immediately — `selectLastSyncSummary`
   * reads `state.dashboardView.isSyncing`, which is only set by the V1 thunk.
   * Without this, clicking "Retry" reproduced the chip-lying defect AC9 fixes.
   * The thunk performs incremental sync (forceRefresh=false); reset is a
   * separate user gesture via the Reset menu item.
   */
  const handlePanelRetry = useCallback(() => {
    if (!token || !id) return
    void dispatch(syncDashboardThunk(token, id))
  }, [token, id, dispatch])

  /**
   * §38.7 comparisonOnly restore button handler (YoY Slice 3.1).
   *
   * CEO axiom (2026-05-27 / feedback_no_direct_mutation_buttons_post_dashboard_save):
   * Does NOT dispatch a direct filter mutation. Sends a natural-language message
   * to the active edit session AI — the BE stages `pendingMutation` (change_filter_values
   * to add the prior season), FSM moves to `awaiting_confirmation`, and the user
   * confirms "yes" in chat.
   *
   * Prior-season derivation:
   *   1. From `editFlowContext.domainFilterValues` — the current season value at
   *      the time of the restore click. When `seasonId` is a string year (e.g. `'2025'`),
   *      prior = `String(Number(currentSeason) - 1)` = `'2024'`.
   *   2. Fallback: `String(new Date().getFullYear() - 1)` when the flow context is
   *      absent (edit session not yet bootstrapped) or when the season key is unrecognised.
   *
   * The `sendEditChatMessageThunk` thunk internally guards on `sessionId` being present.
   * When the session is not yet bootstrapped, it dispatches `setError` and returns false.
   * The ChatPanel already surfaces the error state. We still fire the thunk (rather than
   * adding a guard here) so that:
   *   (a) the guard stays DRY (single source of truth in the service)
   *   (b) the bootstrap auto-triggers on ChatOverlay mount, so by the time the user
   *       can click the button (panel is visible → canvas is loaded → bootstrap fired),
   *       the session will typically be active.
   *
   * The handler is stable (useCallback deps: token, editFlowContext, dispatch) — changes
   * when token/context change, which is correct.
   */
  const handleRestoreComparisonSeason = useCallback(() => {
    if (!token) return

    // Derive prior season from the current edit context's filter values.
    // domainFilterValues keys are plain filter codes (e.g. 'seasonId', 'season').
    // We scan for any key containing 'season' and try to parse the year.
    let priorSeason: string | null = null
    const filterValues = editFlowContext?.domainFilterValues
    if (filterValues) {
      for (const [key, value] of Object.entries(filterValues)) {
        if (key.toLowerCase().includes('season')) {
          const raw = Array.isArray(value) ? value[0] : value
          const parsed = parseInt(raw as string, 10)
          if (!isNaN(parsed)) {
            priorSeason = String(parsed - 1)
            break
          }
        }
      }
    }
    // Calendar-year fallback when context is absent or season key not found.
    if (!priorSeason) {
      priorSeason = String(new Date().getFullYear() - 1)
    }

    void dispatch(sendEditChatMessageThunk(token, `Compare to ${priorSeason}`))
  }, [token, editFlowContext, dispatch])

  // ── Edit-session bootstrap on overlay open ───────────────────────────────
  //
  // When the user toggles the overlay open and there is no active edit session
  // for the current dashboard, dispatch bootstrapEditSessionThunk to open one.
  // Subsequent opens of the same overlay (same dashboard, same page load) are
  // no-ops — the thunk guards against re-bootstrapping an existing session.
  //
  // AC #6: closing the overlay does NOT clear the session. Only navigation to
  // a different dashboard calls clearEditSession (Effect 1 below). This ensures
  // the edit context persists across open/close cycles within one dashboard view.
  const handleAskTcsAiToggle = useCallback(() => {
    const nextOpen = !isViewingChatOpen
    setIsViewingChatOpen(nextOpen)

    if (nextOpen && token && id) {
      // Bootstrap if: no session at all, or session belongs to a different dashboard
      const sessionMissing =
        editSessionId === null || editSessionDashboardId !== id
      if (sessionMissing) {
        void dispatch(bootstrapEditSessionThunk(token, id))
      }
    }
  }, [isViewingChatOpen, token, id, editSessionId, editSessionDashboardId, dispatch])

  // Auto-bootstrap edit session when creation completes — so subsequent chat
  // messages route through the edit path immediately, without requiring the
  // user to toggle the overlay off and back on.
  //
  // Belt-and-suspenders trigger: fires when EITHER signal indicates completion:
  //
  //   1. flowState === 'complete'  (from dashboardCreationSlice, populated by
  //      loadChatSession from session.flowState on cold load — Bug 4 wire-format
  //      fix in tcs-api commit 16bb159a). This is the RELIABLE signal for the
  //      cold-load-after-creation path: when a user navigates directly to an
  //      already-complete dashboard, loadChatSession dispatches setFlowState
  //      ('complete') and this effect fires during mount, silently pre-warming
  //      the edit session.
  //
  //   2. creationStatus === 'complete'  (from chatSessionSlice, set by
  //      confirmPanels after the live "Build My Dashboard" button click). This
  //      covers the immediate-after-creation path where the user just completed
  //      the creation flow in the same page session. creationStatus does NOT
  //      reliably reach 'complete' on cold load (nothing sets it there) — which
  //      is why Bug 18 originally fired only on the live path and not on reload.
  //
  // Double-bootstrap is not possible: bootstrapEditSessionThunk has an
  // idempotency guard (checks existing.sessionId !== null && existing.dashboardId
  // === dashboardId — if true, it returns early without a network call). So
  // even if both signals are true simultaneously, or this effect races with the
  // AskTcsAiToggle handler, only one bootstrap network call will occur.
  useEffect(() => {
    if (!token || !id) return
    // Use flowState (cold-load reliable) OR creationStatus (live creation path).
    // See comment block above for the full rationale.
    const creationIsDone = flowState === 'complete' || creationStatus === 'complete'
    if (!creationIsDone) return
    const noEditSessionForThisDashboard =
      editSessionDashboardId === null || editSessionDashboardId !== id
    if (noEditSessionForThisDashboard) {
      void dispatch(bootstrapEditSessionThunk(token, id))
    }
  }, [token, id, flowState, creationStatus, editSessionDashboardId, dispatch])

  // Track 2: Scope expansion handler — called when user accepts the out-of-scope prompt.
  // This is the single owner of the Redux transition for expand: the child component
  // (OutOfScopeFilterAdjustPrompt) delegates here and does NOT dispatch expandScope itself.
  //
  // v1 scope: we only clear the prompt from state (expandScope) — the real backend call
  // for filter expansion is scheduled for v1.01 (POST /v1/dashboards/:id/filters). The
  // `prompt` argument is intentionally accepted now so the contract matches the child
  // and `prompt.filterCode` / `prompt.requested` are in scope when v1.01 wires the API.
  // See plans/2026-04-22-ai-dashboard-v1-combined-tdd.md §3d.5.
  const handleExpandScope = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- `prompt` held for v1.01 API wiring (see JSDoc above)
    (_prompt: OutOfScopePrompt) => {
      dispatch(dashboardViewActions.expandScope())
    },
    [dispatch],
  )

  // v3: Delete dashboard handler (confirmation-gated)
  const handleDeleteDashboard = useCallback(async () => {
    if (!token || !id) return
    setIsDeletingDashboard(true)
    const res = await DashboardsAPI.delete(token, id)
    setIsDeletingDashboard(false)
    setDeleteConfirmOpen(false)
    if (!res.err) {
      navigate('/dashboards')
    }
  }, [token, id, navigate])

  if (dashboardLoading) {
    return <Loading />
  }

  if (!selectedDashboard && !loading) {
    return (
      <div className="protectedPageLayout">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-lg font-semibold text-primary mb-4">Dashboard not found.</p>
          <Link to="/dashboards" className="text-accent font-medium underline">
            Back to Dashboards
          </Link>
        </div>
      </div>
    )
  }

  // Slice F bugbash 2026-05-06: suppress the legacy "Sync failed for X
  // — credentials may be expired" top alert when AuthExpiredBanner is the
  // canonical surface. The legacy compat shim still dispatches via
  // setSyncError(string) for downstream consumers (see TODO in
  // DashboardSyncService.handleSyncComplete) but rendering both banners
  // duplicates the message. AuthExpiredBanner covers the auth case
  // exclusively at the canvas level.
  const showErrorBanner =
    !anySyncActive &&
    syncError !== null &&
    !bannerDismissed &&
    !authExpiredActive
  const hasCredentialError = syncError != null && /credential/i.test(syncError)
  const showPanelErrorBanner = panelDataError !== null && !panelErrorDismissed

  return selectedDashboard && (
    <div className="ml-64 flex flex-col h-screen overflow-hidden">
      {/* Building overlay — shown while panel creation confirmation call is in-flight */}
      {isBuilding && <BuildingOverlay />}
      <CreateOrUpdateDashboardModal
        isOpen={isRenameDashboardModalOpen}
        onClose={() => setIsRenameDashboardModalOpen(false)}
        mode="rename"
        dashboard={selectedDashboard}
      />

      {/* ── Top bar (Sync button + chat toggle + ⋮ Reset+Delete only) ── */}
      <div className="sticky top-0 z-20 bg-card border-b border-border flex items-center gap-4 px-6 py-3 flex-shrink-0">
        <Link
          to="/dashboards"
          className="text-secondary hover:text-primary transition-colors flex-shrink-0"
          aria-label="Back to dashboards"
        >
          <ChevronLeft size={16} />
        </Link>

        {/* Dashboard title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              className="group flex items-center gap-2 min-w-0 text-left rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
              onClick={() => setIsRenameDashboardModalOpen(true)}
              aria-label="Edit dashboard name"
            >
              <h1 className="text-lg font-bold text-primary truncate">{selectedDashboard.name}</h1>
              <Pencil
                size={14}
                className="text-secondary flex-shrink-0 opacity-70 group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </div>
        </div>

        {/* Top-bar right controls */}
        <div className="flex items-center gap-2 flex-shrink-0">

          {/* LastSyncChip — Slice D. Left of Sync button per DQ1 answer.
              Always visible; reflects current sync state from selectLastSyncSummary. */}
          <LastSyncChip />

          {/* Sync button — three states:
              1. Rate-limited (Slice C): disabled with countdown "Retry in Ns"
              2. Auth-expired (Slice E): destructive "Sync failed" — reads dashboardError?.code === 'AUTH_EXPIRED'
              3. Default: normal "Sync" button */}
          {id && token && (() => {
            const isRateLimited = rateLimitedUntil != null && rateLimitSecondsLeft > 0

            const buttonBaseClassName =
              'inline-flex items-center gap-1.5 h-[34px] px-3 rounded-[7px] border-[1.5px]' +
              'text-xs font-semibold whitespace-nowrap transition-all duration-150' +
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2'

            if (isRateLimited) {
              return (
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  className={cn(buttonBaseClassName, "flex items-center gap-1.5")}
                  aria-label={`Retry sync — rate limited, ${rateLimitSecondsLeft} seconds remaining`}
                  data-testid="btn-sync-rate-limited"
                >
                  <Clock size={14} aria-hidden="true" />
                  Retry in {rateLimitSecondsLeft}s
                </Button>
              )
            }

            if (authExpiredActive) {
              return (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(buttonBaseClassName, "flex items-center gap-1.5 border-border text-destructive bg-destructive-foreground")}
                  aria-label="Sync failed — click to reconnect CampMinder"
                  onClick={() => { void dispatch(syncDashboardThunk(token, id)) }}
                  data-testid="btn-sync-error"
                >
                  <AlertCircle size={14} aria-hidden="true" />
                  Sync failed
                </Button>
              )
            }

            return (
              <Button
                variant="outline"
                size="sm"
                className={cn(buttonBaseClassName, "flex items-center gap-1.5 border-border text-secondary bg-foreground")}
                aria-label="Sync dashboard data from CampMinder"
                onClick={() => { void dispatch(syncDashboardThunk(token, id)) }}
                data-testid="btn-sync"
              >
                <RefreshCw size={14} aria-hidden="true" />
                Sync
              </Button>
            )
          })()}

          {/* AI chat overlay toggle — v2 (brand guide §36).
              On open: dispatches bootstrapEditSessionThunk when no active
              edit session exists for this dashboard (AC #1). */}
          <AskTcsAiToggle
            isOverlayOpen={isViewingChatOpen}
            toggleButtonRef={toggleButtonRef}
            onClick={handleAskTcsAiToggle}
          />

          {/* Change D-2/D-3: ⋮ menu — Reset + Delete ONLY (no Dashboard settings) */}
          <ActionMenu
            menuType="vertical"
            triggerAriaLabel="Dashboard options"
            items={[
              {
                label: 'Reset Dashboard',
                onClick: () => setResetConfirmOpen(true),
                icon: <RefreshCw className="text-accent" size={14} />,
              },
              {
                label: 'Delete Dashboard',
                onClick: () => setDeleteConfirmOpen(true),
                divider: true,
                icon: <Trash2 className="text-destructive" size={14} />,
              },
            ]}
          />
        </div>
      </div>

      {/* Sync error banner — below header, above panel grid */}
      {showErrorBanner && (
        <div
          role="alert"
          className="flex items-center gap-3 px-6 py-3 bg-destructive/10 border-b border-destructive flex-shrink-0"
        >
          <AlertCircle size={16} className="text-destructive flex-shrink-0" />
          <span className="flex-1 text-sm text-destructive">
            {syncError}
            {hasCredentialError && (
              <>
                {' '}
                <Link
                  to="/credentials"
                  className="font-semibold underline hover:no-underline text-destructive"
                >
                  Update Credentials →
                </Link>
              </>
            )}
          </span>
          <button
            aria-label="Dismiss error"
            className="text-destructive"
            onClick={() => setBannerDismissed(true)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Panel data error banner — shown when batch panel fetch fails */}
      {showPanelErrorBanner && (
        <div
          role="alert"
          className="flex items-center gap-3 px-6 py-3 bg-destructive/10 border-b border-destructive flex-shrink-0"
        >
          <AlertCircle size={16} className="text-destructive flex-shrink-0" />
          <span className="flex-1 text-sm text-destructive">{panelDataError}</span>
          <button
            aria-label="Dismiss panel error"
            className="text-destructive"
            onClick={() => setPanelErrorDismissed(true)}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Slice C: Rate-limit countdown banner.
          CEO-locked stacking rule (2026-05-05, §5.4 #4, no-silent-failures §34.1):
          RateLimitBanner is ALWAYS topmost. AuthExpiredBanner renders independently
          below it — BOTH banners visible simultaneously when RATE_LIMIT + AUTH_EXPIRED
          are both active. Banner auto-dismisses at countdown=0; no user action needed. */}
      {rateLimitedUntil != null && rateLimitSecondsLeft > 0 && (
        <RateLimitBanner
          // PR #335 review fix: read the rate-limit vendor from its own slot.
          // Previously this guessed via authExpiredEnvelope?.vendor which is
          // null in the canonical RATE_LIMIT-only scenario, leaving the
          // banner reading "Hold on — Vendor is rate-limiting us."
          vendor={
            rateLimitVendor
              ? rateLimitVendor.charAt(0).toUpperCase() + rateLimitVendor.slice(1)
              : 'Vendor'
          }
          rateLimitedUntil={rateLimitedUntil}
        />
      )}

      {/* Slice E: AUTH_EXPIRED banner — new canonical component superseding
          SyncFailedReconnectBanner (brand guide §34.5).
          Trigger: dashboardError?.code === 'AUTH_EXPIRED' (envelope-driven).
          Stacking: renders independently below RateLimitBanner per §34.3.
          Dismissed state resets when AUTH_EXPIRED error clears (Effect above). */}
      {authExpiredActive && !authBannerDismissed && (
        <AuthExpiredBanner
          code={
            authExpiredEnvelope?.code === 'AUTH_INVALID'
              ? 'AUTH_INVALID'
              : 'AUTH_EXPIRED'
          }
          onReconnect={() => { void handleReconnect() }}
          onDismiss={() => setAuthBannerDismissed(true)}
        />
      )}

      {/* Slice E: Per-domain error banners — category-aware (passes errorEnvelope).
          AUTH_EXPIRED and RATE_LIMIT domains are handled by canvas-level banners above;
          domain banners cover SCHEMA_DRIFT, TRANSIENT_5XX, NETWORK, PLATFORM. */}
      {failedDomains.length > 0 && (
        <DomainErrorBannerStack
          // PR #335 review fix: synthesise a minimal errorEnvelope from the
          // domainStatus so DomainErrorBanner.getCategoryConfig can route by
          // code. Previously errorCode was dropped here and every banner fell
          // through to the default TRANSIENT_5XX variant regardless of the
          // real category.
          failedDomains={failedDomains.map(d => ({
            domainId: d.domainId,
            displayName: resolveDomainDisplayName(d.domainId),
            errorEnvelope: d.errorCode
              ? {
                  code: d.errorCode,
                  message: d.errorUserMessage,
                  vendor: d.domainId,
                }
              : null,
          }))}
          retryingDomainIds={retryingDomainIds}
          onRetry={handleRetryDomain}
        />
      )}

      {/* Slice 3.2 — credential-aware availability sync-error banner.
          Renders below the canvas header, above the panel grid (per brand
          guide §37f position contract). Mounts when the most recent
          `sync:dashboard:complete` event reported one or more domains whose
          backing credential probed as `unavailable` (BE skipped those
          domains and preserved their cached panel data bit-identically per
          TDD §0.3). Dismissible — residual signals (sync-status dot, Sync
          button outline variant, "(cached)" suffix on affected panels'
          date-meta footer) persist after dismiss. */}
      {showSyncErrorBanner && (
        <SyncErrorBanner
          failedDomainNames={domainsFailed.map(d =>
            resolveDomainDisplayName(d.domainId),
          )}
          onDismiss={() => dispatch(dashboardViewActions.dismissSyncError())}
        />
      )}

      {/* ── Main body: full-width canvas + fixed chat overlay ── */}
      {/* v2 layout (brand guide §36): canvas is always full width.
          The chat overlay is position:fixed — it does not push the canvas. */}
      <div className="flex-1 overflow-hidden">

        {/* Canvas area — always full width (no layout change when overlay opens) */}
        <div className="overflow-y-auto bg-background h-full">
          <PanelCanvasV1
            panels={panels}
            showStaleNote={authExpiredActive}
            staleAsOf={staleAsOf}
            onPanelRetry={handlePanelRetry}
            // Gate on edit-session readiness so ComparisonOnlyDisabledPanel's
            // disabled-button branch (`disabled={!onRestore}`) fires when the
            // session isn't yet bootstrapped. Without this gate the prop was
            // always defined and the disabled affordance was unreachable —
            // clicks would fall through to sendEditChatMessageThunk's internal
            // sessionId guard, surfacing the error in the chat panel rather
            // than preventing the click at the button.
            onRestoreComparisonSeason={editSessionId != null ? handleRestoreComparisonSeason : undefined}
          />
        </div>
      </div>

      {/* Chat overlay — position:fixed, always in DOM for Redux state continuity.
          Slides in/out via translateX. Inert when closed. No backdrop. */}
      {id && (
        <ChatOverlay
          dashboardId={id}
          isOpen={isViewingChatOpen}
          onClose={() => setIsViewingChatOpen(false)}
          onExpandScope={handleExpandScope}
          toggleButtonRef={toggleButtonRef}
        />
      )}

      {/* ── AlertDialog: Reset dashboard (confirmation-gated) ── */}
      <ConfirmationModal
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        actionButtonText="Reset"
        dialogTitle="Reset dashboard data?"
        dialogSubtitle="This will wipe all cached data and re-fetch everything from CampMinder. Your panels and configuration are preserved. This cannot be undone."
        variant={ButtonVariant.DESTRUCTIVE}
        onConfirm={() => {
          if (token && id) void dispatch(resetDashboardThunk(token, id))
        }}
      />

      {/* ── AlertDialog: Delete dashboard (confirmation-gated) ── */}
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={open => {
          if (!open) setDeleteConfirmOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this dashboard?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selectedDashboard?.name}</strong> and all its
              panels. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingDashboard} onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeletingDashboard}
              onClick={() => { void handleDeleteDashboard() }}
            >
              {isDeletingDashboard ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PR 6: HarvestModal — reused for in-flow reconnect when sync fails with 401.
          Reopens the CampMinder credential harvest flow without navigating to /credentials.
          onSuccess and onDismiss both close the modal; the sync state drives the banner.
          advanceCreationFlowOnSuccess={false}: this is a reconnect, not a new dashboard
          creation — do not touch the creation FSM or it corrupts the next create session. */}
      <HarvestModal
        isOpen={reconnectModalOpen}
        onClose={() => setReconnectModalOpen(false)}
        onSuccess={() => setReconnectModalOpen(false)}
        onPasteKey={() => setReconnectModalOpen(false)}
        advanceCreationFlowOnSuccess={false}
      />
    </div>
  )
}

export default DashboardCanvas
